import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SearchRetrieveRecord } from "./SearchRetrieveRecord";
import { useSearchParams } from "react-router-dom";
import { SearchParametersDwr } from "../ls/LSSearch";
import axios from "axios";
import { GaleResponse } from "./GaleSearchResponse";

interface GaleSearchContextType {
  setPage: (page: number) => void;
  searchParams: { page: number; term: string };
  totalHits: Readonly<number | null>;
  results: Readonly<SearchRetrieveRecord[] | null>;
  resultsKey: Readonly<string | null>;
  loadingResults: Readonly<boolean>;
  pageSize: number;
}

const GaleSearchContext = createContext<GaleSearchContextType | undefined>(
  undefined
);

export const useGaleSearch = () => {
  const ctx = useContext(GaleSearchContext);
  if (!ctx) {
    throw new Error("useGaleSearch must be used within a GaleSearchProvider");
  }
  return ctx;
};

export const GaleSearchProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingResults, setLoadingResults] = useState<boolean>(false);
  const [totalHits, setTotalHits] = useState<number | null>(null);
  const [results, setResults] = useState<SearchRetrieveRecord[] | null>(null);
  const pageSize = 12;

  const resultsKey = useMemo(() => {
    if (!results) {
      return null;
    }
    return results
      .map((r) => r.identifiers[0]?.uri?.match(/doc\/([^/]+)/)?.[1]) //order by document
      .filter(Boolean)
      .sort()
      .join(",");
  }, [results]);

  const state = useMemo(() => {
    const term = searchParams.get("term") || "";
    const pageParam = parseInt(searchParams.get("page") || "1", 10);
    const page = pageParam < 1 ? 1 : pageParam;

    return {
      term,
      page,
    };
  }, [searchParams]);

  useEffect(() => {
    if (!state) {
      setResults(null);
      setTotalHits(null);
      return;
    }

    setLoadingResults(true);
    const { term, page } = state;
    search(term, page).then((response) => {
      setTotalHits(response.totalHits);
      setResults(response.resources);
      setLoadingResults(false);
    });
  }, [state.page, state.term]);

  const search = async (term: string, page: number) => {
    const startIndex = page <= 1 ? 1 : (page - 1) * pageSize + 1;
    const payload: SearchParametersDwr = {
      searchTerm: term,
      hitsPerPage: pageSize,
      startIndex,
      sortCriteria: "",
    };

    try {
      const { data } = await axios.post<GaleResponse>(`/gale/search`, payload);
      return data;
    } catch (e: unknown) {
      return {
        success: false,
        totalHits: 0,
        resources: [],
      };
    }
  };

  const setPage = (page: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (page <= 1) {
        newParams.delete("page");
      } else {
        newParams.set("page", page.toString());
      }
      return newParams;
    });
  };

  const contextValue: GaleSearchContextType = useMemo(() => {
    return {
      setPage,
      searchParams: {
        page: state.page,
        term: state.term,
      },
      totalHits,
      results,
      resultsKey,
      loadingResults,
      pageSize,
    };
  }, [state, totalHits, results, resultsKey, loadingResults, setPage]);

  return (
    <GaleSearchContext.Provider value={contextValue}>
      {children}
    </GaleSearchContext.Provider>
  );
};

export default GaleSearchProvider;
